import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, of, Subject, tap, first, map } from 'rxjs';
import { MenuChangeEvent } from './api/menuchangeevent';
import { ModuleControllerService } from '../../../projects/api-swagger-client/src/api/moduleController.service';
import { ModuleControllerFindFormatted200Response, ModuleResponseWithRelations } from '@api-swagger-client/index';

@Injectable({
    providedIn: 'root'
})
export class MenuService {
    private menuSource = new Subject<MenuChangeEvent>();
    private resetSource = new Subject();
    private menuData: any[] = [];
    private menuLoaded$ = new BehaviorSubject<boolean>(false);
    private loadingMenu = false;

    menuSource$ = this.menuSource.asObservable();
    resetSource$ = this.resetSource.asObservable();

    constructor(
        private moduleControllerService: ModuleControllerService
    ) { }

    onMenuStateChange(event: MenuChangeEvent) {
        this.menuSource.next(event);
    }

    reset() {
        this.menuData = [];
        this.resetSource.next(true);
    }

    setMenuData(data: any[]): void {
        this.menuData = data;
        this.menuLoaded$.next(true);
    }

    getMenuData(): any[] {
        return this.menuData;
    }

    /**
     * Asegura que los datos del menú estén cargados antes de buscar permisos
     * @returns Observable<boolean> que emite true cuando los datos estén disponibles
     */
    ensureMenuLoaded(): Observable<boolean> {
        // Si ya tenemos datos, devolvemos true inmediatamente
        if (this.menuLoaded$.value && this.menuData.length > 0) {
            return of(true);
        }

        // Si ya estamos cargando, espera a que termine
        if (this.loadingMenu) {
            return this.menuLoaded$.pipe(
                first(loaded => loaded)
            );
        }

        // Si no tenemos datos y no estamos cargando, inicia la carga
        this.loadingMenu = true;
        return this.moduleControllerService.moduleControllerFindFormatted().pipe(
            tap((response: ModuleControllerFindFormatted200Response) => {
                if (!response.success || response.statusCode !== 200) {
                    throw new Error('Error en la respuesta del servidor');
                }
                this.menuData = response.data;
                this.menuLoaded$.next(true);
                this.loadingMenu = false;
            }),
            map(() => true),
            catchError((error) => {
                console.error('Error fetching menu data:', error);
                this.loadingMenu = false;
                return of(false);
            })
        );
    }

    /**
     * Busca y devuelve los permisos asociados a una ruta específica
     * @param path La ruta para la que se buscan permisos
     * @returns Array de permisos para la ruta especificada
     */
    getPermissionsForRoute(path: string): string[] {
        const searchPermissions = (items: any[]): string[] => {
            for (const item of items) {
                // Verificar si routerLink existe y si la ruta coincide exactamente
                if (item.routerLink && item.routerLink[0] === path) {
                    return item.permissions || [];
                }
                // Búsqueda recursiva si hay subitems
                if (item.items) {
                    const foundPermissions = searchPermissions(item.items);
                    if (foundPermissions.length) {
                        return foundPermissions;
                    }
                }
            }
            return [];
        };

        // Si el modelo no está cargado, devolver array vacío
        if (!this.menuData || !this.menuData.length) {
            return [];
        }

        // Buscar en todos los elementos del menú
        for (const menu of this.menuData) {
            const permissions = searchPermissions(menu.items || []);
            if (permissions.length) {
                return permissions;
            }
        }
        return [];
    }

    getItems = ({
        active,
        direction,
        pageIndex,
    }: any = {}): Observable<ModuleResponseWithRelations[]> => {
        return this.moduleControllerService.moduleControllerFindFormatted().pipe(
            tap((response: ModuleControllerFindFormatted200Response) => {
                if (!response.success || response.statusCode !== 200) {
                     ;
                    throw new Error('Error en la respuesta del servidor');
                }
                // Almacenar los datos en el servicio
                this.setMenuData(response.data);
            }),
            catchError((error) => {
                console.error('Error fetching companies:', error);
                return [];
            })
        );
    };
}
